import { prefersReducedMotion } from '@theme/utilities';

/**
 * `.resource-card__link` is an absolutely-positioned overlay covering the whole card for
 * click-through navigation, so `.resource-card__media` never receives a real `:hover`/
 * `pointerenter` (it's a sibling of the hit element, not an ancestor). Track pointer position
 * against the media's bounding box instead, delegated from `document` since cards can be
 * injected dynamically (Rebuy widgets, predictive search, etc.). Keyboard focus is handled
 * separately since it isn't tied to pointer position.
 */

/** @type {Element | null} */
let activeCard = null;
/** @type {boolean} */
let isOverMedia = false;
/** @type {Element | null} */
let focusedCard = null;

/**
 * @param {Element} card
 * @param {Element} media
 */
function hideMedia(card, media) {
  if (card instanceof HTMLElement) card.style.removeProperty('--resource-card-secondary-image-opacity');

  const deferredMedia = media.querySelector('[data-card-hover-video]');
  if (!(deferredMedia instanceof HTMLElement)) return;

  const pauseMedia = deferredMedia.pauseMedia;
  if (typeof pauseMedia === 'function') pauseMedia.call(deferredMedia);
}

/**
 * @param {Element} card
 * @param {Element} media
 */
function revealMedia(card, media) {
  const deferredMedia = media.querySelector('[data-card-hover-video]');
  if (!(deferredMedia instanceof HTMLElement)) {
    // Plain secondary image: nothing to load, reveal immediately.
    if (card instanceof HTMLElement) card.style.setProperty('--resource-card-secondary-image-opacity', '1');
    return;
  }

  if (prefersReducedMotion()) return;

  const playMedia = deferredMedia.playMedia;
  const loadContent = deferredMedia.loadContent;
  if (typeof playMedia !== 'function' || typeof loadContent !== 'function') return;

  // Wait for the video to have an actual frame ready before revealing it, otherwise the poster
  // is hidden (deferred-media[autoplay] poster rule) and the media area goes blank while the
  // video buffers, since (unlike PDP/collection cards) there's no cover image kept underneath.
  if (deferredMedia.getAttribute('data-media-ready') === 'true') {
    playMedia.call(deferredMedia);
    if (card instanceof HTMLElement) card.style.setProperty('--resource-card-secondary-image-opacity', '1');
    return;
  }

  if (deferredMedia.getAttribute('data-media-loaded')) {
    playMedia.call(deferredMedia);
  } else {
    loadContent.call(deferredMedia, false);
  }

  const observer = new MutationObserver(() => {
    if (deferredMedia.getAttribute('data-media-ready') !== 'true') return;
    observer.disconnect();
    const stillActive = (activeCard === card && isOverMedia) || focusedCard === card;
    if (stillActive && card instanceof HTMLElement) {
      card.style.setProperty('--resource-card-secondary-image-opacity', '1');
    }
  });
  observer.observe(deferredMedia, { attributes: true, attributeFilter: ['data-media-ready'] });
}

/**
 * @param {PointerEvent} event
 */
function handlePointerMove(event) {
  // Hover-video reveal is for mouse/trackpad only; touch has no "leave" and would get stuck.
  if (event.pointerType !== 'mouse') return;

  const target = event.target;
  const card =
    target instanceof Element ? target.closest('.resource-card:has(.resource-card__image--secondary)') : null;

  if (card !== activeCard) {
    if (activeCard && isOverMedia && focusedCard !== activeCard) {
      const previousMedia = activeCard.querySelector('.resource-card__media');
      if (previousMedia) hideMedia(activeCard, previousMedia);
    }
    activeCard = card;
    isOverMedia = false;
  }

  if (!card) return;

  const media = card.querySelector('.resource-card__media');
  if (!media) return;

  const rect = media.getBoundingClientRect();
  const inMedia =
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom;

  if (inMedia === isOverMedia) return;
  isOverMedia = inMedia;
  if (inMedia) {
    revealMedia(card, media);
  } else if (focusedCard !== card) {
    hideMedia(card, media);
  }
}

function handlePointerLeaveViewport() {
  if (!activeCard || !isOverMedia) return;

  if (focusedCard !== activeCard) {
    const media = activeCard.querySelector('.resource-card__media');
    if (media) hideMedia(activeCard, media);
  }

  activeCard = null;
  isOverMedia = false;
}

/**
 * @param {FocusEvent} event
 */
function handleFocusIn(event) {
  const target = event.target;
  const card =
    target instanceof Element ? target.closest('.resource-card:has(.resource-card__image--secondary)') : null;
  if (!card) return;

  focusedCard = card;
  const media = card.querySelector('.resource-card__media');
  if (media) revealMedia(card, media);
}

/**
 * @param {FocusEvent} event
 */
function handleFocusOut(event) {
  const target = event.target;
  const card =
    target instanceof Element ? target.closest('.resource-card:has(.resource-card__image--secondary)') : null;
  if (!card) return;

  const relatedTarget = event.relatedTarget;
  if (relatedTarget instanceof Node && card.contains(relatedTarget)) return;

  focusedCard = null;
  if (activeCard === card && isOverMedia) return;

  const media = card.querySelector('.resource-card__media');
  if (media) hideMedia(card, media);
}

document.addEventListener('pointermove', handlePointerMove, { passive: true });
document.addEventListener('pointerleave', handlePointerLeaveViewport, { passive: true });
document.addEventListener('focusin', handleFocusIn, { passive: true });
document.addEventListener('focusout', handleFocusOut, { passive: true });
